import Enumerable from "linq";
import {
  ISize,
} from "../../../base/domain/drawing";
import { IClonable } from "../../../base/domain/system";
import {
  IPropertyPresentable,
  IPropertyEditable,
  PropertyPresenter,
  PropertyEditor,
  instanceOfIPropertyEditable,
} from "../../../base/domain/system/edit";
import { IFormatSpecification } from "./IFormatSpecification";
import {
  FormatPageStructureEditor,
} from "./FormatPageStructureEditor";
import {
  instanceOfISizePropertyEditable,
} from "./properties";
import {
  IEditableFormatImage,
  IEditableFormatLayer,
} from "./IEditableFormatStructure";

export class FormatPagesStructureEditor implements IClonable<FormatPagesStructureEditor> {
  constructor(source: FormatPageStructureEditor[] | FormatPagesStructureEditor) {
    this._source = (source instanceof FormatPagesStructureEditor)
      ? source._source
      : source;
    this._size = this.createPropertyEditor(
      instanceOfISizePropertyEditable,
      s => s.size(),
      vs => ({
        width: this.toValueOrNull(vs, v => v.width),
        height: this.toValueOrNull(vs, v => v.height),
      }),
      (v, p, n) => ({
        width: this.toUpdatedValue(v, p, n, s => s.width),
        height: this.toUpdatedValue(v, p, n, s => s.height),
      }));
    this._isHorizontal = this.createPropertyEditor(
      (s): s is { isHorizontal(): IPropertyEditable<boolean>; } => s
        && s.isHorizontal
        && instanceOfIPropertyEditable<boolean>(s.isHorizontal()),
      s => s.isHorizontal(),
      vs => this.toValueOrNull(vs, v => v),
      (v, p, n) => this.toUpdatedValue(v, p, n, s => s));
    this._image = this.createPropertyEditor(
      (s): s is { image(): IPropertyEditable<IEditableFormatImage>; } => s
        && s.image
        && instanceOfIPropertyEditable<IEditableFormatImage>(s.image()),
      s => s.image(),
      vs => ({
        location: {
          x: this.toValueOrNull(vs, v => v.location.x),
          y: this.toValueOrNull(vs, v => v.location.y),
        },
        size: {
          width: this.toValueOrNull(vs, v => v.size.width),
          height: this.toValueOrNull(vs, v => v.size.height),
        },
      }),
      (v, p, n) => ({
        location: {
          x: this.toUpdatedValue(v, p, n, s => s.location.x),
          y: this.toUpdatedValue(v, p, n, s => s.location.y),
        },
        size: {
          width: this.toUpdatedValue(v, p, n, s => s.size.width),
          height: this.toUpdatedValue(v, p, n, s => s.size.height),
        },
      }));
    this._layer = this.createPropertyEditor(
      (s): s is { layer(): IPropertyEditable<IEditableFormatLayer>; } => s
        && s.layer
        && instanceOfIPropertyEditable<IEditableFormatLayer>(s.layer()),
      s => s.layer(),
      vs => ({
        formatId: this.toValueOrNull(vs, v => v.formatId),
        sectionNo: this.toValueOrNull(vs, v => v.sectionNo),
        imageGuid:  this.toValueOrNull(vs, v => v.imageGuid),
        location: {
          x: this.toValueOrNull(vs, v => v.location.x),
          y: this.toValueOrNull(vs, v => v.location.y),
        },
        size: {
          width: this.toValueOrNull(vs, v => v.size.width),
          height: this.toValueOrNull(vs, v => v.size.height),
        },
        fileName: this.toValueOrNull(vs, v => v.fileName),
      }),
      (v, p, n) => ({
        formatId: this.toUpdatedValue(v, p, n, s => s.formatId),
        sectionNo: this.toUpdatedValue(v, p, n, s => s.sectionNo),
        imageGuid: this.toUpdatedValue(v, p, n, s => s.imageGuid),
        location: {
          x: this.toUpdatedValue(v, p, n, s => s.location.x),
          y: this.toUpdatedValue(v, p, n, s => s.location.y),
        },
        size: {
          width: this.toUpdatedValue(v, p, n, s => s.size.width),
          height: this.toUpdatedValue(v, p, n, s => s.size.height),
        },
        fileName: this.toUpdatedValue(v, p, n, s => s.fileName)
      }));
    this._shouldPrint = this.createPropertyEditor(
      (s): s is { shouldPrint(): IPropertyEditable<boolean>; } => s
        && s.shouldPrint
        && instanceOfIPropertyEditable<boolean>(s.shouldPrint()),
      s => s.shouldPrint(),
      vs => this.toValueOrNull(vs, v => v),
      (v, p, n) => this.toUpdatedValue(v, p, n, s => s));
    this._shouldPrintOnFirstRegistered = this.createPropertyEditor(
      (s): s is { shouldPrintOnFirstRegistered(): IPropertyEditable<boolean>; } => s
        && s.shouldPrintOnFirstRegistered
        && instanceOfIPropertyEditable<boolean>(s.shouldPrintOnFirstRegistered()),
      s => s.shouldPrintOnFirstRegistered(),
      vs => this.toValueOrNull(vs, v => v),
      (v, p, n) => this.toUpdatedValue(v, p, n, s => s));
    this._editingOnly = this.createPropertyEditor(
      (s): s is { editingOnly(): IPropertyEditable<boolean>; } => s
        && s.editingOnly
        && instanceOfIPropertyEditable<boolean>(s.editingOnly()),
      s => s.editingOnly(),
      vs => this.toValueOrNull(vs, v => v),
      (v, p, n) => this.toUpdatedValue(v, p, n, s => s));
  }

  private toValueOrNull<TSource, TValue>(source: TSource[], toValue: (s: TSource) => TValue): TValue {
    if (!source || (source.length <= 0)) {
      return null;
    }
    const value = toValue(source[0]);
    return Enumerable.from(source).any(s => toValue(s) !== toValue(source[0]))
      ? null
      : value;
  }

  private toUpdatedValue<TSource, TValue>(source: TSource, original: TSource, updated: TSource, toValue: (s: TSource) => TValue): TValue {
    return (toValue(original) !== toValue(updated))
      ? toValue(updated)
      : toValue(source);
  }

  private readonly _source: FormatPageStructureEditor[];
  private readonly _size: IPropertyEditable<ISize>;
  private readonly _isHorizontal: IPropertyEditable<boolean>;
  private readonly _image: IPropertyEditable<IEditableFormatImage>;
  private readonly _layer: IPropertyEditable<IEditableFormatLayer>;
  private readonly _shouldPrint: IPropertyEditable<boolean>;
  private readonly _shouldPrintOnFirstRegistered: IPropertyEditable<boolean>;
  private readonly _editingOnly: IPropertyEditable<boolean>;

  getSpecification(): IFormatSpecification {
    return this.toValueOrNull(this._source, s => s.getSpecification());
  }

  source(): FormatPageStructureEditor[] {
    return this._source;
  }

  size(): IPropertyEditable<ISize> {
    return this._size;
  }

  isHorizontal(): IPropertyEditable<boolean> {
    return this._isHorizontal;
  }

  image(): IPropertyEditable<IEditableFormatImage> {
    return this._image;
  }

  layer(): IPropertyEditable<IEditableFormatLayer> {
    return this._layer;
  }

  shouldPrint(): IPropertyEditable<boolean> {
    return this._shouldPrint;
  }

  shouldPrintOnFirstRegistered(): IPropertyEditable<boolean> {
    return this._shouldPrintOnFirstRegistered;
  }

  editingOnly(): IPropertyEditable<boolean> {
    return this._editingOnly;
  }

  protected createPropertyPresenter<T>(get: (s: FormatPageStructureEditor[]) => T): IPropertyPresentable<T> {
    return new PropertyPresenter({
      source: this._source,
      read: s => get(s),
    });
  }

  protected createPropertyEditor<T, TEditor>(isEditor: (s: any) => s is TEditor, toEditor: (s: TEditor) => IPropertyEditable<T>, toPreviousValue?: (vs: T[]) => T, toValue?: (v: T, p: T, n: T) => T): IPropertyEditable<T> {
    const read = (s: FormatPageStructureEditor[]) => {
      const values = Enumerable.from(s)
        .select(e => isEditor(e) ? toEditor(e) : null)
        .where(e => !!e)
        .select(e => e.getValue())
        .toArray();
      return (0 < values.length)
        ? (toPreviousValue ? toPreviousValue(values) : this.toValueOrNull(values, v => v))
        : null;
    };
    return new PropertyEditor({
      source: this._source,
      read,
      write: v => {
        for (const e of this._source) {
          if (isEditor(e)) {
            const editor = toEditor(e);
            editor.setValue(toValue ? toValue(editor.getValue(), read(this._source), v) : v);
          }
        }
      },
      isWritable: s => Enumerable.from(s).any(e => isEditor(e) && toEditor(e).isEditable()),
    });
  }

  clone(): FormatPagesStructureEditor {
    return new FormatPagesStructureEditor(this);
  }
}
